import React, { useState, useEffect } from "react";
import { BaseDialog } from "../../shared/BaseDialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FormService, FormField } from "@shared/services/FormServices";
import { Info } from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface AddSuffixTransformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetField: string;
  fileData: string[];
  onApplyTransformation: (config: any) => void;
}

const AddSuffixTransformationDialog: React.FC<
  AddSuffixTransformationDialogProps
> = ({ isOpen, onClose, targetField, fileData, onApplyTransformation }) => {
  const [formData, setFormData] = useState({
    suffixText: "",
    applyTo: "all",
    conditionType: "",
    conditionValue: "",
  });

  const [previewData, setPreviewData] = useState<{
    before: string[];
    after: string[];
  }>({
    before: [],
    after: [],
  });

  useEffect(() => {
    if (fileData) {
      const samples = fileData.filter((s) => s && s !== "...").slice(0, 3);
      setPreviewData({ before: samples, after: samples });
    }
  }, [fileData]);

  const fields: FormField[] = [
    {
      name: "suffixText",
      type: "text",
      label: "Suffix Text",
      placeholder: "e.g., .pdf, kg, (verified), -COMPLETE",
      helperText: "Text to add at the end of the field value",
      className: "col-span-1",
    },
    {
      name: "applyTo",
      type: "select",
      label: "Apply To",
      options: [
        { value: "all", label: "All Rows" },
        { value: "non_empty", label: "Non-Empty Rows Only" },
        { value: "conditional", label: "Based on Condition" },
      ],
      helperText: "Choose which rows should receive the suffix",
      className: "col-span-1",
    },
    {
      name: "conditionType",
      type: "select",
      label: "Condition Type",
      showWhen: (formValues) => formValues.applyTo === "conditional",
      options: [
        { value: "contains", label: "Contains Text" },
        { value: "starts_with", label: "Starts With" },
        { value: "ends_with", label: "Ends With" },
        { value: "length_greater", label: "Length Greater Than" },
        { value: "length_less", label: "Length Less Than" },
      ],
      helperText: "Select the condition to check",
      className: "col-span-1",
    },
    {
      name: "conditionValue",
      type: formData.conditionType?.includes("length") ? "number" : "text",
      label: "Condition Value",
      showWhen: (formValues) =>
        formValues.applyTo === "conditional" && formValues.conditionType,
      placeholder: formData.conditionType?.includes("length")
        ? "e.g., 10"
        : "e.g., Done, Active",
      helperText: formData.conditionType?.includes("length")
        ? "Number of characters"
        : "Text to match",
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);
    updatePreview(newFormData);
  };

  const checkCondition = (text: string, condition: any): boolean => {
    if (!condition.conditionType || !condition.conditionValue) return true;
    switch (condition.conditionType) {
      case "contains":
        return text.includes(condition.conditionValue);
      case "starts_with":
        return text.startsWith(condition.conditionValue);
      case "ends_with":
        return text.endsWith(condition.conditionValue);
      case "length_greater":
        return text.length > Number(condition.conditionValue);
      case "length_less":
        return text.length < Number(condition.conditionValue);
      default:
        return true;
    }
  };

  const updatePreview = (data: any) => {
    if (!data.suffixText) {
      setPreviewData((prev) => ({ ...prev, after: [] }));
      return;
    }
    const results = previewData.before.map((item) => {
      if (data.applyTo === "all") return item + data.suffixText;
      if (data.applyTo === "non_empty")
        return item ? item + data.suffixText : item;
      if (data.applyTo === "conditional")
        return checkCondition(item, data) ? item + data.suffixText : item;
      return item;
    });
    setPreviewData((prev) => ({ ...prev, after: results }));
  };

  const handleApply = () => {
    if (!formData.suffixText) return;

    onApplyTransformation({
      targetField: toSnakeCase(targetField),
      transformation: {
        type: "add_suffix",
        order: 1,
        config: {
          suffix: formData.suffixText,
          apply_to: formData.applyTo,
          ...(formData.applyTo === "conditional" && {
            condition_type: formData.conditionType,
            condition_value: formData.conditionValue,
          }),
        },
      },
    });
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add Suffix Configuration"
      className="max-w-4xl w-full max-h-[90vh] flex flex-col"
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Add Suffix:</strong> Add text to the end of field values.
            Use for file extensions (.pdf), units (kg), or status indicators
            (verified).
          </div>
        </div>

        <DynamicForm
          fields={fields}
          form={form}
          config={{
            fields,
            layout: "vertical",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={handleFieldChange}
          gridColumns={1}
        />

        {/* Preview */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <h4 className="font-medium mb-3 text-gray-900">Preview</h4>
          <div className="grid grid-cols-2 gap-6">
            {/* Before */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Before</p>
              <div className="space-y-2">
                {previewData.before.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-3 rounded-md border border-gray-200"
                  >
                    <span className="text-sm text-gray-800">
                      {item || "(empty)"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* After */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">After</p>
              <div className="space-y-2">
                {previewData.after.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-3 rounded-md border border-gray-200"
                  >
                    <span className="text-sm text-gray-800">
                      {item || previewData.before[idx] || "(empty)"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!formData.suffixText}>
            Apply Transformation
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default AddSuffixTransformationDialog;
